import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import * as Yup from 'yup'
import { KTSVG } from '../../../_metronic/helpers'
import { constraintConfig } from '../../../constraintConfig'
import { selectAuth, setOriginalEmail, startImpersonating } from '../../../features/authSlice'
import { handleSetUser } from '../../../helpers/SetUserHelper'
import { useAppDispatch } from '../../../hooks'
import { useImpersonateUserMutation } from '../../../services/authApi'
import labelKey from "../../localization/label.json"
import tosterKey from "../../localization/toster.json"

type Props = {
    show: boolean
    handleClose: () => void
    data: any
}
const ImpersonateUserModal = ({ show, handleClose, data }: Props) => {
    const auth = useSelector(selectAuth);
    const [impersonateUser, { data: imersonateData, isLoading, isSuccess, isError, error }] = useImpersonateUserMutation()
    const location = useLocation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    localStorage.setItem("impersonatePath", location.pathname);
    const initialValues = {
        email: ''
    }
    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email('Wrong email format')
            .min(3, 'Minimum 3 symbols')
            .max(50, 'Maximum 50 symbols')
            .required('Email is required'),
    })
    const [updateId, setUpdateId] = useState(0);

    useEffect(() => {
        if (data) {
            setUpdateId(data?.userAccountID);
            formik.setValues({ ...data })
        } else {
            setUpdateId(0);
            formik.resetForm();
        }
    }, [data]);
    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema,
        onSubmit: (values, { resetForm }) => {
            localStorage.setItem("impersonationInProgress", 'true');
            dispatch(setOriginalEmail(auth.email || ''));
            impersonateUser({ email: values.email })
            formik.resetForm();
        }
    })

    useEffect(() => {
        if (isSuccess) {
            handleSetUser({
                dispatch,
                data: imersonateData,
                overrides: {
                    isHomeAddressPopup: false,
                    isCreditCardInfoPopup: false,
                }
            });
            dispatch(startImpersonating());
            const redirectMap = {
                10: "/ap-approved",
                13: "/portal-support-requests",
                [constraintConfig.roleID.roleExecutive]: "/approval-queue"
            };
            const roleID = imersonateData.result.userAccountBasicInfo.roleID;
            const redirectPath = redirectMap[roleID] || "/dashboard";
            setTimeout(() => {
                navigate(redirectPath);
                localStorage.removeItem("impersonationInProgress");
            }, 0);

            caches.open('api-cache').then((cache) => {
                cache.keys().then((keys) => {
                    keys.forEach((request) => {
                        cache.delete(request);
                    });
                });
            });

            toast.success(tosterKey.impersonateLoginSuccessfully);
            handleClose();
        }
    }, [isSuccess]);


    useEffect(() => {
        if (isError && error) {
            let errorMessage: string = 'Something went wrong';
            localStorage.removeItem("impersonationInProgress");
            if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
                errorMessage = (error.data as { message: string }).message;
            }
            toast.error(errorMessage);
            handleClose();
        }
    }, [isError, error]);

    const ssOrgId = data?.userAccountID ?? data?.orgUserID ?? updateId ?? '—'
    const userEmail = data?.email || '—'
    const userRole = data?.userRole?.roleName || data?.roleName || data?.role?.title || '—'

    return (
        <>
            <Modal
                aria-hidden='true'
                dialogClassName='modal-dialog modal-md modal-dialog-centered'
                show={show}
                onHide={handleClose}
            >
                <form
                    className='form w-100'
                    onSubmit={formik.handleSubmit}
                >
                    <div className='modal-header py-5'>
                        <h2 className='mb-0'>{labelKey.impersonateLogin}</h2>
                        <Tooltip id='impersonate-login-modal-actions' place='bottom' />
                        <button
                            type='button'
                            className='btn btn-sm btn-icon btn-active-color-primary'
                            onClick={handleClose}
                            data-tooltip-id='impersonate-login-modal-actions'
                            data-tooltip-content='Close'
                            aria-label='Close'
                        >
                            <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
                        </button>
                    </div>
                    <div className='modal-body py-5'>
                        <p className='fs-5 fw-semibold mb-4'>
                            {labelKey.areYouSureToImpersonateLogin} with{' '}
                            <span className='text-primary text-capitalize'>
                                {data?.firstName} {data?.middleName} {data?.lastName}?
                            </span>
                        </p>
                        <div className='d-flex flex-column gap-2 fs-6'>
                            <div className='d-flex align-items-baseline'>
                                <span className='fw-semibold me-2' style={{ minWidth: '6.5rem' }}>{labelKey.ssOrgId}:</span>
                                <span>{ssOrgId}</span>
                            </div>
                            <div className='d-flex align-items-baseline'>
                                <span className='fw-semibold me-2' style={{ minWidth: '6.5rem' }}>{labelKey.email}:</span>
                                <span className='text-lowercase text-break'>{userEmail}</span>
                            </div>
                            <div className='d-flex align-items-baseline'>
                                <span className='fw-semibold me-2' style={{ minWidth: '6.5rem' }}>{labelKey.role}:</span>
                                <span>{userRole}</span>
                            </div>
                        </div>
                    </div>
                    <div className='modal-footer py-4'>
                        <button
                            type='button'
                            className='btn btn-light'
                            onClick={handleClose}
                        >
                            {labelKey.cancel}
                        </button>
                        <button
                            type='submit'
                            className='btn btn-primary'
                            disabled={isLoading}
                        >
                            {!isLoading && <span className='indicator-label'>{labelKey.yes}</span>}
                            {isLoading && (
                                <span className='indicator-progress' style={{ display: 'block' }}>
                                    {labelKey.pleaseWait}...{' '}
                                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                            )}
                        </button>
                    </div>
                </form>
            </Modal>
        </>
    )
}

export default ImpersonateUserModal
